import type {
  CategoriesBulkPayload,
  ClearableCategoryField,
  DisciplineCategory,
  ResultFormula,
} from "../../../lib/queries/rules";
import { formatSeconds, parseTimeInput } from "../../../lib/rulesLabels";
import type { ID } from "../../../lib/id";

/**
 * The combination editor writes through one bulk endpoint whose fields
 * are three-valued — leave alone, inherit the discipline default, or set
 * a value — and this module derives which of the three was meant instead
 * of asking.
 *
 * The form holds one plain string per field and remembers what it held
 * when the dialog opened. Comparing the two says everything:
 *
 *   unchanged        → the field is not mentioned, so it keeps whatever
 *                      each target had. This is what makes "set the time
 *                      limit for a whole row" safe when the cells differ
 *                      in every other field.
 *   changed to ""    → listed in `clear`, back to the discipline default.
 *   RESET            → listed in `clear` too, and this is the one that
 *                      makes a bulk edit work at all.
 *   changed to value → listed in `set`.
 *
 * Editing one cell starts from that cell's own overrides; editing many
 * starts from empty, because the targets need not agree.
 *
 * That empty start is why RESET exists. In a bulk edit every field begins
 * at "", so "" already means "leave each target alone" — and clearing a
 * field that is already empty is not a change, which left "put this whole
 * column back on the discipline default" unreachable: the user picked the
 * „Výchozí" option, nothing differed from the initial value, and the field
 * was never mentioned in the payload. RESET is a value the field cannot
 * otherwise hold, so choosing it is always a change.
 */

/**
 * The explicit "put this back on the discipline default" value.
 *
 * Deliberately a string no real field can hold — a floor id, a formula key
 * and a time are all rendered as plain strings, and this is none of them —
 * so it never collides with a legitimate value.
 */
export const RESET = "\u0000reset";

/**
 * The nine override fields plus the note and the enabled flag, all as
 * form strings so one comparison rule covers every one of them.
 */
export interface CategoryFormValues {
  /** "on" | "off", or "" for "leave each target as it is". */
  enabled: string;
  time_min_seconds: string;
  time_max_seconds: string;
  floor_id: string;
  recording_id: string;
  plus_judges: string;
  tech_judges: string;
  result_formula: string;
  deduction_table_id: string;
  scoring_sheet_id: string;
  note: string;
}

export type CategoryField = keyof CategoryFormValues;

export const OVERRIDE_FIELDS = [
  "deduction_table_id",
  "scoring_sheet_id",
  "time_min_seconds",
  "time_max_seconds",
  "floor_id",
  "recording_id",
  "plus_judges",
  "tech_judges",
  "result_formula",
] as const satisfies readonly ClearableCategoryField[];

export const FIELD_LABELS: Record<CategoryField | "enabled", string> = {
  enabled: "Stav kombinace",
  time_min_seconds: "Nejkratší čas",
  time_max_seconds: "Nejdelší čas",
  floor_id: "Soutěžní plocha",
  recording_id: "Nahrávka ČNSMS",
  plus_judges: "Plusových porotců",
  tech_judges: "Technických porotců",
  result_formula: "Výsledek",
  deduction_table_id: "Srážková tabulka",
  scoring_sheet_id: "Hodnoticí tabulka",
  note: "Poznámka",
};

const EMPTY: CategoryFormValues = {
  enabled: "",
  time_min_seconds: "",
  time_max_seconds: "",
  floor_id: "",
  recording_id: "",
  plus_judges: "",
  tech_judges: "",
  result_formula: "",
  deduction_table_id: "",
  scoring_sheet_id: "",
  note: "",
};

const text = (v: number | string | undefined): string => (v === undefined ? "" : String(v));

/**
 * What the form starts with. For a single cell that is the cell's own
 * overrides — an empty field means it inherits — and for several cells
 * it is empty throughout, since they may disagree and the editor only
 * applies what the user actually changes.
 */
export function initialValues(single?: DisciplineCategory): CategoryFormValues {
  if (!single) return { ...EMPTY };
  return {
    enabled: single.enabled ? "on" : "off",
    time_min_seconds:
      single.time_min_seconds === undefined ? "" : formatSeconds(single.time_min_seconds),
    time_max_seconds:
      single.time_max_seconds === undefined ? "" : formatSeconds(single.time_max_seconds),
    floor_id: text(single.floor_id),
    recording_id: text(single.recording_id),
    plus_judges: text(single.plus_judges),
    tech_judges: text(single.tech_judges),
    result_formula: text(single.result_formula),
    deduction_table_id: text(single.deduction_table_id),
    scoring_sheet_id: text(single.scoring_sheet_id),
    note: single.note,
  };
}

/** Per-field validation messages, for the fields holding a value. */
export function categoryFormErrors(values: CategoryFormValues): Partial<Record<string, string>> {
  const errors: Partial<Record<string, string>> = {};
  for (const field of ["time_min_seconds", "time_max_seconds"] as const) {
    if (values[field] === RESET) continue;
    if (values[field] !== "" && parseTimeInput(values[field]) === null) {
      errors[field] = "Zadejte čas ve tvaru m:ss.";
    }
  }
  const from = parseTimeInput(values.time_min_seconds);
  const to = parseTimeInput(values.time_max_seconds);
  if (
    !errors.time_min_seconds &&
    !errors.time_max_seconds &&
    from !== null &&
    to !== null &&
    from > to
  ) {
    errors.time_min_seconds = "Nejkratší čas nesmí být delší než nejdelší.";
  }
  for (const field of ["plus_judges", "tech_judges"] as const) {
    if (values[field] === RESET) continue;
    if (values[field] !== "" && !/^\d+$/.test(values[field].trim())) {
      errors[field] = "Zadejte celé nezáporné číslo.";
    }
  }
  return errors;
}

/** Renders the changed fields as one bulk request. */
export function buildBulkPayload(
  categoryIds: ID[],
  initial: CategoryFormValues,
  current: CategoryFormValues,
): CategoriesBulkPayload {
  const payload: CategoriesBulkPayload = { category_ids: categoryIds };
  if (current.enabled !== initial.enabled && current.enabled !== "") {
    payload.enabled = current.enabled === "on";
  }

  const clear: ClearableCategoryField[] = [];
  const set: NonNullable<CategoriesBulkPayload["set"]> = {};

  for (const field of OVERRIDE_FIELDS) {
    const value = current[field].trim();
    // RESET is checked before the unchanged test: in a bulk edit the field
    // starts at "" and an emptied field is therefore not a change, so the
    // sentinel is the only way to reach `clear`.
    if (value === RESET) {
      clear.push(field);
      continue;
    }
    if (value === initial[field].trim()) continue;
    if (value === "") {
      clear.push(field);
      continue;
    }
    switch (field) {
      case "time_min_seconds":
      case "time_max_seconds":
        set[field] = parseTimeInput(value) ?? 0;
        break;
      case "plus_judges":
      case "tech_judges":
        set[field] = Number(value);
        break;
      case "deduction_table_id":
      case "scoring_sheet_id":
      case "floor_id":
      case "recording_id":
        set[field] = value;
        break;
      case "result_formula":
        set[field] = value as ResultFormula;
        break;
    }
  }
  // The note has no inherited value, so an emptied note is a set to ""
  // rather than a reset.
  if (current.note.trim() !== initial.note.trim()) set.note = current.note.trim();

  if (clear.length > 0) payload.clear = clear;
  if (Object.keys(set).length > 0) payload.set = set;
  return payload;
}

/** True when the form would not change anything. */
export function isNoOp(payload: CategoriesBulkPayload): boolean {
  return payload.enabled === undefined && !payload.clear && !payload.set;
}
